import { useEffect, useState } from 'react';

export type EmailToastStatus = 'sending' | 'success' | 'error' | 'not_configured' | null;

interface EmailStatusToastProps {
  status: EmailToastStatus;
  onClose: () => void;
}

const CONFIGS = {
  sending: {
    icon: 'ri-mail-send-line',
    iconBg: 'bg-indigo-100 text-indigo-600',
    barBg: 'bg-indigo-500',
    title: 'Enviando listas...',
    message: 'Estamos enviando las listas al correo de Club Rayo',
    border: 'border-indigo-200',
    autoClose: false,
  },
  success: {
    icon: 'ri-mail-check-line',
    iconBg: 'bg-emerald-100 text-emerald-600',
    barBg: 'bg-emerald-500',
    title: '¡Email enviado!',
    message: 'Las listas fueron enviadas a clubrayovolantines@gmail.com',
    border: 'border-emerald-200',
    autoClose: true,
  },
  error: {
    icon: 'ri-mail-close-line',
    iconBg: 'bg-red-100 text-red-600',
    barBg: 'bg-red-500',
    title: 'Error al enviar',
    message: 'No se pudo enviar el email. Las listas igualmente fueron generadas.',
    border: 'border-red-200',
    autoClose: true,
  },
  not_configured: {
    icon: 'ri-mail-settings-line',
    iconBg: 'bg-amber-100 text-amber-600',
    barBg: 'bg-amber-500',
    title: 'Email no configurado',
    message: 'Configurá EmailJS para activar el envío automático de listas.',
    border: 'border-amber-200',
    autoClose: true,
  },
};

export default function EmailStatusToast({ status, onClose }: EmailStatusToastProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (status) {
      setVisible(true);
      const cfg = CONFIGS[status];
      if (cfg.autoClose) {
        const t = setTimeout(() => {
          setVisible(false);
          setTimeout(onClose, 300);
        }, 4500);
        return () => clearTimeout(t);
      }
    } else {
      setVisible(false);
    }
  }, [status, onClose]);

  if (!status) return null;

  const cfg = CONFIGS[status];

  return (
    <div
      className={`fixed bottom-6 right-6 z-50 transition-all duration-300 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
      }`}
    >
      <div
        className={`bg-white rounded-xl border ${cfg.border} w-80 overflow-hidden`}
        style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.10)' }}
      >
        {/* Barra superior de color */}
        <div className={`h-1 ${cfg.barBg} ${status === 'sending' ? 'animate-pulse' : ''}`} />

        <div className="p-4 flex items-start gap-3">
          <div className={`w-10 h-10 flex items-center justify-center rounded-lg shrink-0 ${cfg.iconBg}`}>
            <i className={`${cfg.icon} text-xl`}></i>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-gray-900">{cfg.title}</p>
            <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{cfg.message}</p>
          </div>
          <button
            type="button"
            onClick={() => {
              setVisible(false);
              setTimeout(onClose, 300);
            }}
            className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer shrink-0"
          >
            <i className="ri-close-line text-sm"></i>
          </button>
        </div>
      </div>
    </div>
  );
}
